/**
 * The pure half of the sidebar's Chats section and the Chats page.
 *
 * One project can hold a hundred conversations and more, and `GET /threads`
 * returns all of them in one answer (there is no paging, and a thread carries a
 * title and a `created_at` and nothing else). So the sidebar shows only the
 * newest few and the rest live on a page of their own — grouped by day and
 * searchable by title, both of which the client can do with what it already has.
 */
import type { Thread } from "./threads";

/** How many recent conversations the sidebar shows before "View all". */
export const SIDEBAR_RECENT_LIMIT = 8;

/**
 * The current project's conversations for the sidebar: the live ones first,
 * then the newest of the rest, up to the limit.
 *
 * A conversation of this project that is running, or waiting on an approval,
 * shows its state on its own row rather than being pinned somewhere else — so
 * it has to *be* a row. Sorting only by age would hide an old conversation that
 * was resumed below "View all" while it runs; leading with the live ones keeps
 * every one of them in sight, even past the limit. When a run ends the row
 * simply falls back to its place by age. `threads` is already newest-first
 * (`getThreads` sorts it), and that order is kept inside both halves.
 */
export function recentThreads(
  threads: readonly Thread[],
  liveThreadIds: ReadonlySet<string>,
  limit: number = SIDEBAR_RECENT_LIMIT,
): Thread[] {
  const live = threads.filter((thread) => liveThreadIds.has(thread.thread_id));
  const out = [...live];
  for (const thread of threads) {
    if (out.length >= limit) break;
    if (liveThreadIds.has(thread.thread_id)) continue;
    out.push(thread);
  }
  return out;
}

/**
 * A thread's timestamp as a Date, or null.
 *
 * Timestamps come back naive (no zone). Reading them as UTC rather than local
 * keeps the date stable wherever the reader is, and matches what the server
 * recorded.
 */
export function parseThreadTime(iso: string | null): Date | null {
  if (!iso) return null;
  const at = new Date(/[Z+]|-\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(at.getTime()) ? null : at;
}

export type ThreadDayGroup = "today" | "yesterday" | "week" | "earlier";

export const THREAD_DAY_GROUPS: readonly ThreadDayGroup[] = ["today", "yesterday", "week", "earlier"];

/** Midnight at the start of `date`'s day, in the reader's own zone. */
function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Which day group a thread belongs to — by the reader's calendar, not by
 * elapsed hours: a chat from 23:50 last night is "yesterday" at 00:10, not
 * "today". A thread with no usable date is "earlier" rather than dropped: it is
 * still the user's conversation.
 */
export function threadDayGroup(thread: Pick<Thread, "created_at">, now: Date): ThreadDayGroup {
  const at = parseThreadTime(thread.created_at);
  if (!at) return "earlier";
  const days = Math.round((startOfDay(now) - startOfDay(at)) / DAY_MS);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days <= 7) return "week";
  return "earlier";
}

/** The threads in day groups, in group order, empty groups left out. Order inside a group is kept. */
export function groupThreadsByDay(
  threads: readonly Thread[],
  now: Date,
): { group: ThreadDayGroup; threads: Thread[] }[] {
  const buckets = new Map<ThreadDayGroup, Thread[]>();
  for (const thread of threads) {
    const group = threadDayGroup(thread, now);
    const bucket = buckets.get(group);
    if (bucket) bucket.push(thread);
    else buckets.set(group, [thread]);
  }
  return THREAD_DAY_GROUPS.flatMap((group) => {
    const bucket = buckets.get(group);
    return bucket ? [{ group, threads: bucket }] : [];
  });
}

/**
 * Threads whose title contains the query, case-insensitively. Every word of the
 * query must appear, in any order — "wall level" finds "Level 2 wall schedule".
 * An untitled thread matches only the empty query.
 */
export function filterThreads(threads: readonly Thread[], query: string): Thread[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...threads];
  return threads.filter((thread) => {
    const title = (thread.title ?? "").toLowerCase();
    return words.every((word) => title.includes(word));
  });
}
